// =============================================================================
// Activity log helper — one line for any module that wants to record "who did
// what, where, when".
//
// WHY IT EXISTS. The Command Centre has an Activity Log screen. It answers the
// questions you ask when something has gone wrong: who ticked that checklist
// item, who logged in at Arndale this morning, who marked that delivery
// received. Today it reads the Google Sheet, because the Supabase table meant to
// replace it holds three rows — only the Command Centre's own settings gateway
// ever wrote to it. This is the helper that lets every other module start
// writing there too.
//
// WHAT NOW CALLS IT: the presence/login taps in `presence.js`. Nothing else yet —
// the stock, checklist and delivery call sites are deliberately left alone (see
// the note at the bottom of this file). The Command Centre still reads the sheet;
// pointing it at Supabase is a later step and needs writers running first.
//
// HOW TO USE IT, from any module:
//
//     import { record } from "./activity.js";
//     import { supabase } from "./supabaseClient.js";
//     record({ siteKey: SITE.key, action: "checklistTick", target: "Fridge temps",
//              client: supabase });
//
// Call it WITHOUT awaiting. An audit line must never be the reason a staff
// member can't tick something off.
//
// THREE RULES IT OBEYS
//   * It never breaks its caller. Fire-and-forget, every error swallowed, and
//     the database function behind it returns nothing rather than raising.
//   * It never carries a PIN. Who did it is worked out server-side from the
//     login pass, exactly like presence (migration 0040) and stock (0006).
//   * IT WRITES ON WHICHEVER CONNECTION HOLDS THE RIGHT PASS. See `client` below —
//     this is not a style preference, it's the fix for a real defect.
//
// KILL SWITCH: set ACTIVITY_ENABLED to false. That is the whole revert.
//
// SITE ACCESS (29 Sep 2026). On a site-access host (trafford / meadowhall, or
// ?access=on — see siteAccess.js) every line written here also carries WHICH
// DEVICE it came from: `{ device_id, device }` is merged into the caller's meta,
// the caller's own keys winning on a clash. That is how every action — a
// presence tap, a sold-out mark, a stock request, a checklist tick — links to
// its device in the Command Centre, without touching a single call site. On
// every other host the meta is exactly what the caller passed.
// =============================================================================
import { dualRunEnabled } from "./stockSync.js";
import { activityMeta as siteAccessDeviceMeta } from "./siteAccess.js";

export const ACTIVITY_ENABLED = true;

// The event classes the database accepts. Anything else is refused, which is
// what keeps the feed countable — "how many permission refusals last week" has
// to be answerable without a text search.
export const KINDS = [
  "action",             // somebody did a thing: a checklist tick, a delivery, a stock mark
  "login",              // signed in
  "pin_refused",        // a PIN that didn't work
  "permission_denied",  // a valid PIN that wasn't allowed to do that
  "staff_change",       // somebody added, edited or deactivated on the staff list
  "admin_write",        // a Command Centre settings change
  "system",             // something the system itself did
];

// ⚠️ THERE IS NO `?activity=off`, AND THERE MUST NEVER BE ONE.
//
// Every other module in this app has a per-tablet URL escape hatch, and for a
// feature flag that's a good pattern — it lets one shop be pulled out of a
// dual-run without a deploy. An AUDIT LOG is not a feature flag. A URL parameter
// that switches off the record of who did what is a bypass anyone standing at
// the kiosk can use, and the people most motivated to use it are exactly the
// people the log exists to record. The record must not be disableable by the
// person being recorded.
//
// `?activity=on` is kept, deliberately, and the asymmetry is the point: forcing
// logging ON can only ever produce MORE record, never less, so it's useless to
// somebody trying to hide something and useful for testing a single tablet.
//
// To stop the logging you now need a deploy (ACTIVITY_ENABLED below) or a
// server-side per-site config the shop floor can't reach. That is the correct
// cost for switching off an audit trail.
function on() {
  if (!ACTIVITY_ENABLED) return false;
  try {
    if (typeof window !== "undefined") {
      if (new URLSearchParams(window.location.search).get("activity") === "on") return true;
    }
  } catch (e) { /* no window — fall through */ }
  return dualRunEnabled();
}

// record({ siteKey, action, kind, target, detail, meta, dedupeKey })
//
//   siteKey    'tc' | 'arndale' | 'meadowhall' | 'mcr' (legacy 'trafford' /
//              'kitchen' spellings are folded by the database)
//   action     the verb — 'checklistTick', 'stockDelivered', 'login'…
//   kind       one of KINDS above. Defaults to 'action'.
//   target     what it was done to — an item name, a roll, a review id
//   detail     free text
//   meta       anything module-specific, as an object
//   dedupeKey  optional. Pass one and a retried write collapses instead of
//              logging the same thing twice.
//   client     REQUIRED. Which Supabase connection to write on. There is no
//              default — see below.
//
// ⚠️ WHY `client` IS REQUIRED RATHER THAN DEFAULTED. Who an audit row gets signed
// by is decided server-side from the login pass on the connection that sent it.
// The till shares ONE connection for stock, sell-outs and everything else, so a
// module that mints its own pass (presence does, for exactly this reason) must
// write on its OWN connection — otherwise the row can be signed with whoever the
// shared connection last authenticated as, and two staff tapping within half a
// second is all that would take.
//
// An earlier cut of this defaulted to the shared connection when `client` was
// omitted. That is a trap rather than a convenience: it makes the dangerous
// option the one you get by forgetting, and the failure is silent — the row
// still writes, it just has the wrong name on it. So there is no default. Every
// caller states which connection it is writing on, including the ones using the
// shared one:
//
//     import { supabase } from "./supabaseClient.js";
//     record({ siteKey: SITE.key, action: "checklistTick", client: supabase });
//
// One extra token per call site, and the choice becomes impossible to make by
// accident.
//
// The device this line came from, merged into the caller's meta (see SITE ACCESS
// in the header). `device` is `{ device_id, device }` on a site-access host and
// null everywhere else — then the caller's meta comes back untouched, null and
// all. Caller keys win on a clash. A meta that isn't a plain object (an array, a
// string) can't be merged into, so it is left exactly as sent. Pure; the default
// argument is the only part that looks at the page, and it never throws.
export function withDeviceMeta(meta, device = deviceMetaHere()) {
  if (!device || typeof device !== "object") return meta;
  if (meta == null) return { ...device };
  if (typeof meta === "object" && !Array.isArray(meta)) return { ...device, ...meta };
  return meta;
}
function deviceMetaHere() {
  try { return siteAccessDeviceMeta(); } catch (e) { return null; }
}

// Returns a promise so tests can wait on it. Callers should not.
export function record({ siteKey, action, kind = "action", target = null, detail = null, meta = null, dedupeKey = null, client }) {
  if (!on() || !siteKey || !action) return Promise.resolve({ ok: false, skipped: true });
  if (!client || typeof client.rpc !== "function") {
    // Loud in the console, silent to the staff member. A missing connection is a
    // programming mistake, not something a till should ever surface — but it must
    // not quietly fall back to a connection that might sign the row wrongly.
    console.warn("[activity] record() called without a connection — nothing written. Pass `client` explicitly.");
    return Promise.resolve({ ok: false, error: "no client supplied" });
  }
  // ⚠️ The whole body is inside the try, not just the promise chain. A `.catch()`
  // only ever sees a REJECTED promise — if the connection throws SYNCHRONOUSLY
  // (a misconfigured client, a bad URL) the error escapes before there is a
  // promise to catch, and goes straight into whatever the staff member was doing.
  // The one promise this function makes is that it never breaks its caller, and
  // without this that promise was only true most of the time. Found by a test
  // that handed it a connection which throws on the spot.
  // The device merge gets its own guard: whatever it does, the line is still
  // written with (at least) the caller's own meta.
  let pMeta = meta;
  try { pMeta = withDeviceMeta(meta); } catch (e) { pMeta = meta; }
  try {
    return client
      .rpc("activity_record", {
        p_site: siteKey,
        p_kind: KINDS.includes(kind) ? kind : "action",
        p_action: action,
        p_target: target,
        p_detail: detail,
        p_meta: pMeta,
        p_source: "kiosk",
        p_dedupe_key: dedupeKey,
      })
      .then(({ error }) => {
        if (error) {
          console.warn("[activity] write failed:", error.message);
          return { ok: false, error };
        }
        return { ok: true };
      })
      .catch((e) => {
        console.warn("[activity] threw:", e?.message || e);
        return { ok: false, error: e };
      });
  } catch (e) {
    console.warn("[activity] threw before it started:", e?.message || e);
    return Promise.resolve({ ok: false, error: e });
  }
}

// -----------------------------------------------------------------------------
// WHAT IS DELIBERATELY NOT WIRED YET, AND WHY
//
// * Stock, the daily checklist and deliveries. Those call sites live in files
//   other work is actively changing; wiring them from here would mean two
//   changes editing the same lines. They're a follow-on, not a gap in the design
//   — `record()` is the whole interface and each is a one-line call.
//
// * The customer-review "looks wrong?" challenge. The flow lives in `foh.jsx`,
//   a shared file, for the same reason. When it is wired, the call is:
//
//       record({ siteKey: SITE.key, kind: "action", action: "reviewChallenged",
//                target: reviewId, detail: reason, client: supabase });
//
//   Note `target` takes the review id, never the customer's words and never a
//   name — the log records THAT something happened and to WHICH review, and the
//   detail of who said what stays in the disputes table behind its admin gate.
// -----------------------------------------------------------------------------
